import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BaseProperties } from '../../../models';
import { TabContent } from '../../../models/ui-tabs.interfaces';

/**
 * The `Tab` component is a child of the `Tabs` component.
 * It represents one `Tab` item and its content within a panel of other `Tab` items.
 *
 *
 * `Tab` takes a string or `TemplateRef` for the header, and any content for the body of the tab.
 *
 * When the tab is selected the `select` output will be triggered.
 * The `select` output will also be triggered for the active tab when the tabs are loaded or updated.
 *
 *
 * Tab with string header:
 *
 * ```html
 * <tab [config]="{heading: 'tab1'}">
 *    tab 1 content
 * </tab>
 * ```
 *
 * Tab with custom header:
 *
 * ```html
 * <ng-template #tabHeading>
 *    <idp-icon
 *        icon="facebook"
 *        size="sm"
 *        style="margin-right: 7px;">
 *    </idp-icon>
 *    Hello Tab 1
 * </ng-template>
 * <tabs>
 *    <tab>
 *        Tab 1 content <idp-icon icon="alert" size="lg"></idp-icon>
 *    </tab>
 *    <tab>
 *        Tab 2 content
 *    </tab>
 *    <tab>
 *        Tab 3 content
 *    </tab>
 * </tabs>
 * ```
 */
@Component({
  selector: 'idp-tab',
  templateUrl: './tab.component.html'
})
export class TabComponent implements OnInit, BaseProperties<TabContent> {
  @Input() content: TabContent = {} as TabContent;
  @Output() selected: EventEmitter<void> = new EventEmitter<void>();
  /**
   * Indicates whether the `Tab` is active/selected.
   */
  active = false;
  id = '';

  ngOnInit() {
    this.setId();
    if (this.content.active) {
      this.active = this.content.active;
    }
  }

  /**
   * this will set the tab id based on provided index as @Input
   * V.imp since same id for multiple tabs will throw ax violations
   */
  setId() {
    this.id = `idp-tab-${
      this.content && this.content.index ? this.content.index : 0
    }`;
  }

  doSelect() {
    this.selected.emit();
  }

  shouldRender() {
    return this.active;
  }
}
